/**
 * Whether a run has the keys it needs: the run strip's press before a run, and
 * the Go at an ask-first pause while one runs.
 *
 * Both read the SELECTION's key once (`useCanCall`), while a run's moves are
 * routed by role. With the source check routed to a company whose key is not
 * stored, the strip printed "est. $8.82" beside an armed Run that `startRun`
 * then refused; with reasoning routed to a keyed company and the selection
 * keyless, it greyed a Run that would have started. Invariant 4 decides what
 * "ready to run" means when reasoning has a key and the check does not: a run
 * whose dials schedule source checks makes them on the check's route or not at
 * all, and never on the selection in its place, so that run is not ready — and
 * it says which key, and that the checks can be turned off instead. A run whose
 * dials schedule no check is ready without that key, because no pass it can
 * make needs it.
 *
 * `runKeyGap` asks the question `startRun` asks before it spends, in its order,
 * and answers in its sentence, so the strip's line and the press's refusal
 * cannot come to disagree (`runGate.test.ts` holds the two side by side).
 * `gateReady` asks it of the one move a pause proposes.
 */

import type { ModelEntry, Role } from '../core/models'
import type { LogicalOpKind } from '../core/types'
import { entryFor, providerFor, resolveFor, runAuditor } from '../core/store'
import type { RolesInPlay } from '../engine/run'

/** The routing and the keys: the whole of what the answer reads. */
export type RunGateView = Parameters<typeof resolveFor>[0]

/** Why a run would be refused before it spends, for want of a key. */
export interface RunKeyGap {
  /** The role whose route has no key, or `audit`: a cross-examination with no
   *  second company's key to run on. */
  readonly role: Exclude<Role, 'chat'> | 'audit'
  /** `startRun`'s own sentence for that refusal, word for word. */
  readonly text: string
}

/**
 * The first key a run with these dials would be refused for, or null when every
 * pass it can make has one: reasoning always; the source check and the panel
 * only where the dials schedule them; the closing scan only where the budget can
 * reach it (`scanIsReachable`); and, for a cross-examination, a company other
 * than the one reasoning runs on (`runAuditor`, as `startRun` resolves it).
 */
export function runKeyGap(s: RunGateView, roles: RolesInPlay, audits: number): RunKeyGap | null {
  const m = resolveFor(s, 'reason')
  if (!m) {
    return {
      role: 'reason',
      text: `A run makes a pass for every move, so it needs your ${providerFor(s, 'reason').name} key.`,
    }
  }
  for (const role of ['check', 'panel'] as const) {
    if (!roles[role] || resolveFor(s, role)) continue
    return {
      role,
      text:
        `This run schedules ${role === 'check' ? 'source checks' : 'panels'}, and those are routed to ` +
        `${entryFor(s, role).name} — which needs your ${providerFor(s, role).name} key. Add it, or ` +
        `turn them off.`,
    }
  }
  if (roles.scan && !resolveFor(s, 'scan')) {
    return {
      role: 'scan',
      text:
        `Every run ends on a breakthrough scan, and yours is routed to ${entryFor(s, 'scan').name} — ` +
        `which needs your ${providerFor(s, 'scan').name} key. Add it, or point the scan back at your ` +
        `selected model.`,
    }
  }
  if (audits > 0 && !runAuditor(s)) {
    return {
      role: 'audit',
      text: 'A cross-examination needs a key for a second provider — the point is a model from another company, and one from the same one would be the panel again. Turn it off, or add a key.',
    }
  }
  return null
}

/** The route a run's move runs on: the check's for a source check (and the
 *  survey, the link scan, the prior-art search and the field sweep, which search
 *  as it does), the panel's, the scan's for a bridge, and reasoning's for every
 *  move. A cross-examination has no role — it runs on another company's model,
 *  which `gateReady` is handed. A run's prior-art search read reasoning's key at
 *  the ask-first Go while it searched on the check's (D286). */
export const gateRole = (k: Exclude<LogicalOpKind, 'audit'>): Role =>
  k === 'verify' || k === 'survey' || k === 'link' || k === 'priorArt' || k === 'sweep'
    ? 'check'
    : k === 'panel'
      ? 'panel'
      : k === 'bridge'
        ? 'scan'
        : 'reason'

/**
 * Whether the move an ask-first pause proposes has a key on the route it would
 * run on — what its price cell says before Go: the figure, or "Needs a key".
 * The auditor is the one the gate resolved (`auditorFor`), the resolution the
 * pass itself makes.
 */
export function gateReady(s: RunGateView, k: LogicalOpKind, auditor: ModelEntry | null): boolean {
  if (k === 'audit') return auditor !== null
  return resolveFor(s, gateRole(k)) !== null
}
